import {
  CAPTION_CHAR_W,
  CAPTION_H,
  markerBox,
  nameBox,
  plateBox
} from "./labelGeometry";

// Overlays compete for VISUAL CHANNELS. Two overlays on the same channel cannot
// coexist however pretty each is on its own, so the picker is radio-within-a-
// channel and channels are independent. Nothing here hardcodes a list of
// overlays: the channel comes from the data.

// The channel set is defined ONCE, in Ruby, on Overlay::CHANNEL_PARAMS, and
// ships in the payload as `channelParams`. This list is the display ORDER and
// the fallback for a payload that predates a channel — never the definition. A
// channel present in the payload but missing here still renders, at the end.
export const CHANNEL_ORDER = ["motion", "region", "column", "grouping", "marker"];

// These are what a reader sees above each group of lenses, so they say what
// the group DOES. They used to be the database's column names with a capital
// letter on the front: "Groupings" is not a word anybody says out loud, and
// "Markers" tells you nothing about veils and Da'ath.
export const CHANNEL_LABELS = {
  motion: "Read it as a sequence",
  region: "Divide it into regions",
  column: "The pillars",
  grouping: "Group the spheres by",
  marker: "Also show"
};

// And what the empty option in each group is called. Four identical "None"s
// down the rail was the picker's most repeated word.
export const CHANNEL_NONE = {
  motion: "No sequence",
  region: "No regions",
  column: "No pillars",
  grouping: "Ungrouped",
  marker: "Nothing extra"
};

// The order to walk channels in, given whatever the payload actually declared.
// A channel added in Ruby alone used to vanish from the picker and from the
// URL, silently, which reads as a lens that will not stick.
export function channelsInOrder(channelParams) {
  const declared = Object.keys(channelParams || {});
  if (declared.length === 0) return CHANNEL_ORDER;
  const known = CHANNEL_ORDER.filter((channel) => declared.includes(channel));
  const novel = declared.filter((channel) => !CHANNEL_ORDER.includes(channel));
  return [...known, ...novel];
}

export function channelLabel(channel) {
  return CHANNEL_LABELS[channel] || channel;
}

export function channelNoneLabel(channel) {
  return CHANNEL_NONE[channel] || "None";
}

export function isMulti(channel, multiChannels) {
  return (multiChannels || ["marker"]).includes(channel);
}

// Toggle one overlay within its channel. Single channels replace; multi
// channels add and remove. This is the whole rule, and it lives in one place.
export function toggleLens(lenses, overlay, multiChannels) {
  const next = { ...lenses };
  const { channel, slug } = overlay;

  if (isMulti(channel, multiChannels)) {
    const current = next[channel] || [];
    const without = current.filter((s) => s !== slug);
    if (without.length === current.length) next[channel] = [...current, slug];
    else if (without.length) next[channel] = without;
    else delete next[channel];
    return next;
  }

  if (next[channel] === slug) delete next[channel];
  else next[channel] = slug;
  return next;
}

export function isActive(lenses, overlay, multiChannels) {
  const value = lenses[overlay.channel];
  if (value === undefined || value === null) return false;
  return isMulti(overlay.channel, multiChannels)
    ? value.includes(overlay.slug)
    : value === overlay.slug;
}

export function activeOverlays(overlays, lenses, multiChannels) {
  return overlays.filter((o) => isActive(lenses, o, multiChannels));
}

// Only lenses this tradition actually has survive; the rest are dropped rather
// than guessed at, the same rule selection follows across a switch.
export function lensesToQuery(lenses, channelParams, multiChannels) {
  const params = new URLSearchParams();
  channelsInOrder(channelParams).forEach((channel) => {
    const value = lenses[channel];
    if (value === undefined || value === null) return;
    const param = channelParams[channel];
    if (!param) return;
    params.set(param, isMulti(channel, multiChannels) ? value.join(",") : value);
  });
  return params;
}

// ---------------------------------------------------------------------------
// Geometry. All of it works in the diagram's own coordinate space.
// ---------------------------------------------------------------------------

export function nodesFor(group, sephirothBySlug) {
  return group.items
    .filter((item) => item.kind === "sephirah")
    .map((item) => sephirothBySlug.get(item.slug))
    .filter(Boolean);
}

export function bandRect(nodes, radius, pad = 3) {
  if (nodes.length === 0) return null;
  const r = radius + pad;
  const xs = nodes.map((n) => n.x);
  const ys = nodes.map((n) => n.y);
  const x = Math.min(...xs) - r;
  const y = Math.min(...ys) - r;
  return {
    x,
    y,
    width: Math.max(...xs) + r - x,
    height: Math.max(...ys) + r - y,
    rx: r
  };
}

// Andrew's monotone chain.
function convexHull(points) {
  const sorted = [...points].sort((a, b) => (a[0] - b[0]) || (a[1] - b[1]));
  if (sorted.length < 3) return sorted;
  const cross = (o, a, b) =>
    (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);

  const build = (pts) => {
    const stack = [];
    pts.forEach((p) => {
      while (stack.length >= 2 && cross(stack[stack.length - 2], stack[stack.length - 1], p) <= 0) {
        stack.pop();
      }
      stack.push(p);
    });
    stack.pop();
    return stack;
  };

  return [...build(sorted), ...build([...sorted].reverse())];
}

// A soft closed blob around a set of nodes: the convex hull pushed outward past
// each circle, then rounded by joining edge midpoints with quadratic curves.
export function hullShape(nodes, radius, pad = 3) {
  if (nodes.length === 0) return null;
  const r = radius + pad;

  if (nodes.length === 1) {
    return { type: "circle", cx: nodes[0].x, cy: nodes[0].y, r };
  }

  const hull = convexHull(nodes.map((n) => [n.x, n.y]));
  if (hull.length < 3) return { type: "rect", ...bandRect(nodes, radius, pad) };

  const cx = hull.reduce((sum, p) => sum + p[0], 0) / hull.length;
  const cy = hull.reduce((sum, p) => sum + p[1], 0) / hull.length;
  const outward = hull.map(([x, y]) => {
    const dx = x - cx;
    const dy = y - cy;
    const length = Math.hypot(dx, dy) || 1;
    return [x + (dx / length) * r, y + (dy / length) * r];
  });

  const mid = (a, b) => [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
  const start = mid(outward[outward.length - 1], outward[0]);
  let d = `M ${start[0].toFixed(2)} ${start[1].toFixed(2)}`;
  outward.forEach((point, i) => {
    const next = outward[(i + 1) % outward.length];
    const m = mid(point, next);
    d += ` Q ${point[0].toFixed(2)} ${point[1].toFixed(2)} ${m[0].toFixed(2)} ${m[1].toFixed(2)}`;
  });
  return { type: "path", d: `${d} Z` };
}

export function groupShape(group, sephirothBySlug, radius, channel) {
  const nodes = nodesFor(group, sephirothBySlug);
  if (nodes.length === 0) return null;
  // Bands for the axis-aligned channels, hulls for the grouping channel — the
  // difference is what lets a column lens and a region lens read as orthogonal
  // when both are on.
  if (channel === "grouping") return hullShape(nodes, radius, 3);
  const rect = bandRect(nodes, radius, channel === "column" ? 4 : 3);
  return { type: "rect", ...rect };
}

// The steps of a sequence, resolved against the nodes the payload already has.
export function sequenceSteps(overlay, sephirothBySlug, pathsBySlug) {
  const group = overlay.groups[0];
  if (!group) return [];
  return group.items.map((item) => ({
    ...item,
    node:
      item.kind === "sephirah"
        ? sephirothBySlug.get(item.slug)
        : pathsBySlug.get(item.slug)
  }));
}

// ---------------------------------------------------------------------------
// Caption placement, solved rather than hand-tuned.
//
// A segment caption has the same problem the path plates had: the obvious spot
// is already occupied. Hand-tuning one collision creates another — that is how
// path 13's offset ended up exactly on path 19's label. So try candidate
// positions along the segment and take the first that clears everything.
// ---------------------------------------------------------------------------

// Slide along the segment first, then further out from it. A coarse grid found
// no slot for "along 27 · Peh" between the Tiphareth and Yesod names; a fine
// one does. Searching is cheap, hand-placing is what keeps going wrong.
const CANDIDATE_T = [
  0.5, 0.44, 0.56, 0.38, 0.62, 0.32, 0.68, 0.26, 0.74, 0.2, 0.8, 0.15, 0.85
];
const CANDIDATE_OFFSET = [
  6, -6, 8, -8, 10, -10, 12, -12, 14, -14, 16, -16, 18, -18, 21, -21
];

function boxesOverlap(a, b) {
  return a.x1 < b.x2 && b.x1 < a.x2 && a.y1 < b.y2 && b.y1 < a.y2;
}

function overlapArea(a, b) {
  const w = Math.min(a.x2, b.x2) - Math.max(a.x1, b.x1);
  const h = Math.min(a.y2, b.y2) - Math.max(a.y1, b.y1);
  return w > 0 && h > 0 ? w * h : 0;
}

function circleHitsBox(cx, cy, r, box) {
  const nx = Math.min(Math.max(cx, box.x1), box.x2);
  const ny = Math.min(Math.max(cy, box.y1), box.y2);
  return Math.hypot(cx - nx, cy - ny) < r;
}

// `obstacles` is every label box already on the diagram — path plates, sphere
// names, marker labels. Solving against only some of them is how Yesod's name
// ended up under two segment captions.
export function placeCaption(from, to, text, { obstacles, sephiroth, radius, viewBox }) {
  const halfW = (text.length * CAPTION_CHAR_W) / 2;
  const halfH = CAPTION_H / 2;

  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const length = Math.hypot(dx, dy) || 1;
  const nx = dy / length;
  const ny = -dx / length;

  // If nothing is clear, take the LEAST bad rather than the first tried.
  let best = null;
  let bestOverlap = Infinity;

  for (const offset of CANDIDATE_OFFSET) {
    for (const t of CANDIDATE_T) {
      const x = from.x + dx * t + nx * offset;
      const y = from.y + dy * t + ny * offset;
      const box = { x1: x - halfW, y1: y - halfH, x2: x + halfW, y2: y + halfH };

      const insideBox =
        box.x1 >= viewBox.x &&
        box.y1 >= viewBox.y &&
        box.x2 <= viewBox.x + viewBox.width &&
        box.y2 <= viewBox.y + viewBox.height;
      if (!insideBox) continue;

      const hitsSphere = sephiroth.some((s) => circleHitsBox(s.x, s.y, radius + 1, box));
      const overlap = obstacles.reduce(
        (total, other) => total + overlapArea(box, other),
        hitsSphere ? 1e6 : 0
      );

      if (overlap === 0) return { x, y };
      if (overlap < bestOverlap) {
        bestOverlap = overlap;
        best = { x, y };
      }
    }
  }

  return best;
}

// Boxes a segment caption must clear: every plate, every sphere name, every
// marker label. All three come from lib/labelGeometry, which is also what
// Labels.jsx draws from — solving against a box the renderer does not actually
// use is solving the wrong problem.
export function labelObstacles({ paths, sephiroth, markerGroups, radius, viewBox }) {
  return [
    ...paths.map(plateBox),
    ...sephiroth.filter((s) => s.countedInTen !== false).map((s) => nameBox(s, radius)),
    ...(markerGroups || []).map(({ group }) => markerBox(group, viewBox))
  ];
}
